import {
  type ArtifactsResponse,
  type AssetManifest,
  type EquipmentComponent,
  type EquipmentListResponse,
  type InventoryInstanceWire,
  type InventoryResponse,
  ITEM_CATEGORIES,
  type ItemCategory,
  itemDisplayName,
  type LoadoutWire,
  type PetPaletteResponse,
  type PetsResponse,
  type PetUnlockResponse,
  type Rarity,
  type SkinMutationResponse,
  type UpgradeCurrency,
  type UpgradeQuoteWire,
} from '@kenocyber/shared';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  equipComponent,
  fetchArtifacts,
  fetchEquipment,
  fetchInventory,
  fetchPetPalette,
  fetchUpgradeQuote,
  HuntApiError,
  unequipComponent,
  upgradeComponent,
} from '../api/hunt';
import { itemArtUrl } from '../game/assets';
import { ArtifactDetails, ArtifactsTab } from './ArtifactsTab';
import { CardsTab } from './CardsTab';
import { CellBadges } from './CellBadges';
import { EquipmentDetails, EquipmentTab } from './EquipmentTab';
import { InventoryDetails } from './InventoryDetails';
import { JEWEL_ASSET_BASE_URL, JewelPanel } from './JewelPanel';
import { JewelSocketRow } from './JewelSocketRow';
import { PetDetailsPanel } from './PetDetailsPanel';
import { PetsTab } from './PetsTab';
import {
  type IncorporationStatus,
  PrimordialIncorporationDialog,
} from './PrimordialIncorporationDialog';
import { buildShipOptions, incorporationFailureText } from './primordial-incorporation';
import { RARITY_ORDER, rarityLabel, rarityPaint, rarityStyle, rarityTierOf } from './rarity';
import { SkinGachaBanner } from './SkinGachaBanner';
import { SkinWardrobe } from './SkinWardrobe';
import { useCards } from './useCards';
import { useJewels } from './useJewels';
import { useSkinArt } from './useSkinArt';
import { useSkinWardrobe } from './useSkinWardrobe';

/**
 * The inventory, floating over a hunt that keeps running behind it.
 *
 * Absolutely positioned like every other panel, so opening it never resizes
 * or recreates the Phaser canvas: mobs keep descending, the ship keeps
 * firing, the boss keeps counting and the server keeps being authoritative
 * while the player browses.
 */

interface InventoryPanelProps {
  readonly loadout: LoadoutWire | null;
  /** Resolves item art. The same manifest the arena draws from. */
  readonly manifest: AssetManifest;
  readonly onClose: () => void;
  /** Equips an item; resolves once the SERVER confirmed the change. */
  readonly onEquip: (itemId: string, loadoutVersion: number) => Promise<void>;
  /**
   * Merges two copies; resolves once the SERVER confirmed it.
   *
   * Same contract as `onEquip`: nothing is applied locally first. The vault
   * decides which copies were spent and what the replacement is, and this
   * panel only ever renders the answer.
   */
  readonly onCombine: (first: string, second: string) => Promise<void>;
  /** Every pet and what it is doing, as the SERVER derived it. */
  readonly pets: PetsResponse | null;
  /** Activates a pet, or deactivates with `null`. Server-confirmed. */
  readonly onActivatePet: (petInstanceId: string | null) => Promise<void>;
  /** Evolves two copies; resolves once the server confirmed it. */
  readonly onEvolvePet: (first: string, second: string) => Promise<void>;
  /** Feeds one unit of one food to one pet. Server-confirmed. */
  readonly onFeedPet: (petInstanceId: string, foodDefinitionId: string) => Promise<void>;
  /**
   * Consumes one Energy Sphere or Tech Paint Bucket.
   *
   * Resolves with the server's own palette, which the panel adopts verbatim.
   */
  readonly onUnlockPetAppearance: (unlockDefinitionId: string) => Promise<PetUnlockResponse>;
  /** Wears an already-unlocked appearance. Free. */
  readonly onApplyPetAppearance: (
    petInstanceId: string,
    energyId: string,
    finishId: string,
  ) => Promise<void>;
  /**
   * The CONFIRMED answer to a skin equip or unequip (gate V23.2A.1).
   *
   * Called by the wardrobe hook after a 200 the contract's schema validated,
   * with the whole response - which carries the run the same transaction
   * reconfigured. The parent hands its `hunt` half to the hunt session, and
   * the arena adopts it without a second bootstrap.
   *
   * A panel prop rather than a hook this file owns, for the same reason
   * `onEquip` is one: the arena belongs to the parent, and a panel that could
   * reconfigure a run would be a panel with authority over gameplay.
   */
  readonly onSkinConfirmed: (response: SkinMutationResponse) => void;
  /** Bumped by the parent after a successful equip or merge, to force a reload. */
  readonly refreshToken: number;
  /**
   * Há um duelo VIVO agora (gate V28H.G).
   *
   * Desativa as ações de carta e mostra a frase aprovada. É uma cortesia, não
   * a autoridade: o servidor recusa de qualquer maneira, e a recusa dele passa
   * pela mesma frase.
   */
  readonly pvpActive?: boolean;
  /** Há uma caçada a correr: a nova seleção vale na PRÓXIMA. */
  readonly huntActive?: boolean;
}

const CATEGORY_LABEL: Record<ItemCategory | 'all', string> = {
  all: 'Todos',
  ship: 'Naves',
  cannon: 'Canhões',
  artifact: 'Artefatos',
  pet: 'Pets',
  skin: 'Skins',
  consumable: 'Consumíveis',
  equipment: 'Equipamentos',
  /*
   * O separador existe porque o tipo o exige, e é isso que se quer.
   *
   * Nenhuma joia é obtenível - as quinze definições estão desativadas e não há
   * rota, gacha ou drop que crie uma - por isso este separador aparece vazio
   * hoje. O rótulo entra agora para que o dia em que a primeira joia existir
   * não seja o dia em que alguém descobre que o inventário não sabia nomeá-la.
   */
  jewel: 'Joias',
  /*
   * As cartas (gates V28H.C-G).
   *
   * O rótulo entrou vazio no V28H.C, quando nada era obtenível. Deixou de o
   * estar: a caça deixa cair cartas desde o V28H.E, e desde o V28H.G este
   * separador mostra a coleção, os três espaços e a fusão manual.
   */
  card: 'Cartas',
};

type CategoryTab = ItemCategory | 'all';
const TABS: readonly CategoryTab[] = ['all', ...ITEM_CATEGORIES];

/**
 * Which tabs get the enlarged modal, as a function of the tab alone.
 *
 * Equipment is the only one, and it earns it: the tab carries a diagram, a
 * five-row legend, a card grid and a panel of attributes side by side, and at
 * the generic 1040px they all compete for the same space. A grid of small cells
 * gains nothing from another 400px of width, so the other tabs keep theirs.
 *
 * Derived rather than stored, which is the point: there is no state that can
 * hold the wrong answer after a tab change, and a test can pin the rule without
 * mounting the whole inventory.
 */
export function inventoryPanelClass(tab: CategoryTab): string {
  return tab === 'equipment' ? 'inv-panel inv-panel-wide' : 'inv-panel';
}

type Availability = 'any' | 'equipped' | 'available' | 'locked';
const AVAILABILITY_LABEL: Record<Availability, string> = {
  any: 'Tudo',
  equipped: 'Equipado',
  available: 'Disponível',
  locked: 'Bloqueado',
};

/** Cells per row, so the last row can be padded to look like a grid. */
const GRID_COLUMNS = 6;

/**
 * Deterministic order: equipped first, then rarity, then level, then
 * acquisition, with the id as the final tiebreak so two identical items never
 * swap places between renders.
 */
function compareItems(a: InventoryInstanceWire, b: InventoryInstanceWire): number {
  if (a.equipped !== b.equipped) {
    return a.equipped ? -1 : 1;
  }
  const tier = rarityTierOf(b.rarity) - rarityTierOf(a.rarity);
  if (tier !== 0) {
    return tier;
  }
  if (a.level !== b.level) {
    return b.level - a.level;
  }
  // The server already returns acquisition order, so an equal comparison here
  // preserves it; the id keeps that stable when everything else ties.
  return a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0;
}

function matches(
  item: InventoryInstanceWire,
  tab: CategoryTab,
  rarity: Rarity | 'any',
  availability: Availability,
): boolean {
  if (tab !== 'all' && item.category !== tab) {
    return false;
  }
  if (rarity !== 'any' && item.rarity !== rarity) {
    return false;
  }
  switch (availability) {
    case 'equipped':
      return item.equipped;
    case 'available':
      return item.availableForTrade || (!item.equipped && !item.locked);
    case 'locked':
      return item.locked || item.equipped;
    default:
      return true;
  }
}

function ItemCell({
  item,
  manifest,
  selected,
  onSelect,
}: {
  item: InventoryInstanceWire;
  manifest: AssetManifest;
  selected: boolean;
  onSelect: () => void;
}) {
  /*
   * **A arte sai do resolvedor canónico** (gate V28I.BI.2).
   *
   * Era `item.assetKey`, e essa é a chave do manifesto GERAL: ela é nula para
   * uma joia, para uma carta e para uma skin, cuja arte vive num manifesto
   * próprio. Uma joia extraída do mundo aberto — que passou a ser obtenível no
   * V28I.BI — aparecia com `?` numa célula, com a imagem publicada ao lado.
   *
   * `itemArtUrl` pergunta ao MESMO `itemArt` que a carga de campo e as caixas
   * usam, e ele sabe dos quatro manifestos. O recuo continua a existir para uma
   * definição que realmente não tem arte.
   */
  const url = itemArtUrl(manifest, item.definitionId, item.level);
  return (
    <button
      type="button"
      className="inv-cell"
      // The rarity border is a property of the CELL, not of a state: equipped,
      // locked, selected and hovered all layer ON it and never replace it.
      data-rarity={item.rarity}
      data-paint={rarityPaint(item.rarity)}
      data-equipped={item.equipped ? 'true' : 'false'}
      data-locked={item.locked ? 'true' : 'false'}
      data-selected={selected ? 'true' : 'false'}
      data-testid={`inv-cell-${item.itemId}`}
      data-item-id={item.itemId}
      style={rarityStyle(item.rarity)}
      aria-label={`${itemDisplayName(item.definitionId)}, ${rarityLabel(item.rarity)}, nível ${item.level}`}
      aria-pressed={selected}
      onClick={onSelect}
    >
      {/* The second animated ring layer. Empty by design: it exists so the
          top two rarities can stack a paint the two pseudo-elements cannot. */}
      <span className="inv-cell-ring" aria-hidden="true" />
      <span className="inv-cell-art">
        {url ? (
          // `contain` inside a box that is 99% of the cell: the art claims
          // practically the whole square, is never cropped or stretched, and
          // its transparency is never flattened.
          <img src={url} alt="" loading="lazy" draggable={false} />
        ) : (
          <span className="inv-cell-noart" aria-hidden="true">
            ?
          </span>
        )}
      </span>
      {/* One cell is one instance here, so the quantity is always ×1. The
          artifacts tab groups copies and passes the real count. */}
      <CellBadges
        level={item.level}
        quantity={1}
        equipped={item.equipped}
        rarity={item.rarity}
        testIdSuffix={item.itemId}
      />
    </button>
  );
}

export function InventoryPanel({
  loadout,
  manifest,
  onClose,
  onEquip,
  onCombine,
  pets,
  onActivatePet,
  onEvolvePet,
  onFeedPet,
  onUnlockPetAppearance,
  onApplyPetAppearance,
  onSkinConfirmed,
  refreshToken,
  pvpActive = false,
  huntActive = false,
}: InventoryPanelProps) {
  const [items, setItems] = useState<InventoryInstanceWire[]>([]);
  const [stacks, setStacks] = useState<InventoryResponse['stacks']>([]);
  const [artifacts, setArtifacts] = useState<ArtifactsResponse | null>(null);
  const [loadoutVersion, setLoadoutVersion] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<CategoryTab>('all');
  /*
   * The wardrobe owns its own fetch and mutation state.
   *
   * Its shape is genuinely different from the item grid's - a catalogue and a
   * shelf of instances rather than one list - so folding it into the panel's
   * state would mean teaching that state about two contracts. The hook is read
   * only while the skin tab is open, and reset when the panel closes.
   */
  const wardrobe = useSkinWardrobe({ onConfirmed: onSkinConfirmed });
  /*
   * As joias, lidas só quando alguma aba precisa delas (gate V28C).
   *
   * A aba "Joias" mostra as cartas e a das skins mostra os assentos, e ambas
   * leem a MESMA view - por isso o hook vive aqui, uma vez, e não em cada um
   * dos dois componentes. Um jogador que nunca abra nenhuma das duas não paga
   * o pedido.
   */
  const jewels = useJewels({ enabled: tab === 'jewel' || tab === 'skin' });
  /*
   * Qual peça o próximo encaixe usa.
   *
   * A escolha vive aqui e não dentro da lista porque quem a lê é o VESTIÁRIO,
   * noutra aba: seleciona-se uma joia nas "Joias" e encaixa-se nos assentos das
   * skins. O que o pedido envia continua a ser a identidade da instância - a
   * seleção decide QUAL peça, nunca em que posição do ecrã ela estava.
   */
  const [selectedJewelId, setSelectedJewelId] = useState<string | null>(null);
  /*
   * Os três espaços de carta, lidos só quando a aba `Cartas` está aberta
   * (gate V28H.G).
   *
   * A COLEÇÃO não vem daqui: ela é a lista de instâncias que o painel já leu,
   * filtrada por categoria. Uma segunda leitura das mesmas linhas seria a
   * segunda fonte de verdade sobre o que a conta tem.
   */
  const cards = useCards({ enabled: tab === 'card' });
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  /*
   * A incorporação em curso: qual Coração, em que estado, e a última recusa
   * (gate V28F.C).
   *
   * Três estados separados de propósito. Fundi-los num só faria "recusou" e
   * "está a correr" serem o mesmo campo, e o modal precisa dos dois ao mesmo
   * tempo: uma recusa deixa a janela aberta, com a razão escrita e a escolha
   * intacta, pronta a repetir.
   */
  const [incorporatingHeart, setIncorporatingHeart] = useState<string | null>(null);
  const [incorporationStatus, setIncorporationStatus] = useState<IncorporationStatus>('idle');
  const [incorporationFailure, setIncorporationFailure] = useState<string | null>(null);
  /*
   * O nome legível de cada CÓPIA de skin, para uma joia poder dizer onde está.
   *
   * Por instância e não por definição: duas cópias da mesma skin são dois
   * sítios diferentes, e "encaixada em Silver Hunter" seria ambíguo se ambas se
   * chamassem o mesmo. O sufixo é a capacidade, que é a única coisa que as
   * distingue aos olhos de quem as tem.
   */
  /**
   * As naves da conta, tal como o seletor de incorporação as mostra.
   *
   * Saem do INVENTÁRIO e não da lista `ships` da view das joias, e a razão é
   * uma só: o inventário já traz o nível, o estado de reserva, o soulbound e a
   * contagem de corações da mesma linha. Cruzar duas fontes para o mesmo facto
   * seria criar um sítio onde elas podem divergir.
   */
  const shipOptions = useMemo(() => buildShipOptions(items), [items]);

  const skinLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const copy of wardrobe.state.owned) {
      const name =
        wardrobe.state.catalogue.find((entry) => entry.definitionId === copy.definitionId)
          ?.displayName ?? 'Skin';
      labels.set(
        copy.itemInstanceId,
        copy.socketCapacity === null ? name : `${name} (${copy.socketCapacity})`,
      );
    }
    return labels;
  }, [wardrobe.state.owned, wardrobe.state.catalogue]);
  /**
   * The wardrobe's heading, so a reveal can hand focus to the shelf it filled.
   *
   * A ref rather than `document.getElementById`: the tab is unmounted whenever
   * another category is open, and looking an id up would find a stale node from
   * whatever the panel drew last.
   */
  const wardrobeHeading = useRef<HTMLHeadingElement | null>(null);
  /**
   * A skin's picture, from the SKINS sidecar rather than the asset manifest.
   *
   * Resolved once for the whole panel and handed to both the roulette and the
   * shelf, so six cards and a reveal over them make one request between them.
   * `game-assets/skin-manifest.json` is where a skin's PNG is declared, next to
   * its pivot and its VFX descriptor - the main manifest has never carried one,
   * which is why every card drew "sem prévia" until now.
   */
  const skinArtFor = useSkinArt();
  const [rarity, setRarity] = useState<Rarity | 'any'>('any');
  const [availability, setAvailability] = useState<Availability>('any');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<string | null>(null);
  /**
   * The five slots and every component owned, as the SERVER reports them.
   *
   * Adopted verbatim from every answer, never edited locally: an equip, a
   * removal and an upgrade all return the whole state, so there is no window
   * in which the panel and the vault disagree and nothing optimistic is ever
   * shown. The effective values, the Power and the prices in it were all
   * computed on the server.
   */
  const [equipmentState, setEquipmentState] = useState<EquipmentListResponse | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<string | null>(null);
  /**
   * Which component TYPE the available list is restricted to.
   *
   * Separate from the selected item because clicking an empty slot clears the
   * selection - there is no item to describe - and the type the player asked
   * about is precisely what must survive that.
   */
  const [equipmentFocus, setEquipmentFocus] = useState<EquipmentComponent | null>(null);
  const [equipmentQuote, setEquipmentQuote] = useState<UpgradeQuoteWire | null>(null);
  const [equipmentBusy, setEquipmentBusy] = useState(false);
  const [equipmentError, setEquipmentError] = useState<string | null>(null);
  const [equipmentOutcome, setEquipmentOutcome] = useState<string | null>(null);
  const [upgradeCurrency, setUpgradeCurrency] = useState<UpgradeCurrency>('gold');
  const [useStabilizer, setUseStabilizer] = useState(false);
  const [equipping, setEquipping] = useState<string | null>(null);
  const [equipError, setEquipError] = useState<string | null>(null);
  const [combining, setCombining] = useState(false);
  const [combineError, setCombineError] = useState<string | null>(null);
  const [selectedPet, setSelectedPet] = useState<string | null>(null);
  /**
   * The palette, read once the pets tab opens.
   *
   * Deliberately NOT part of the pets response: which colours exist is the
   * catalog's answer and rarely changes, while which are unlocked is this
   * account's and changes when a sphere is spent. Keeping them apart means
   * feeding a pet does not re-send twenty-seven swatches.
   */
  const [palette, setPalette] = useState<PetPaletteResponse | null>(null);
  /**
   * The COPY every pet action applies to, chosen explicitly.
   *
   * Owned here rather than in the details panel so it survives a re-render and
   * an inventory refresh. Nothing resets it implicitly: switching tabs, a
   * filter change or a reload of the pet list must not silently move a
   * player's food onto a different instance.
   */
  const [selectedCopy, setSelectedCopy] = useState<string | null>(null);
  /** The SECOND copy an evolution would consume. Also explicit. */
  const [secondaryCopy, setSecondaryCopy] = useState<string | null>(null);
  /**
   * The copies of the SELECTED pet, as of the last server answer.
   *
   * Kept so the effect below can tell "the selection is gone" from "the list
   * has not loaded yet" - the first needs clearing, the second must not touch
   * anything.
   */
  const previousCopyIds = useRef<readonly string[]>([]);
  const [petBusy, setPetBusy] = useState(false);
  const [petError, setPetError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const titleId = useId();

  const load = useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);
    try {
      // Every page, not just the first: an inventory is not assumed to be
      // small, and the cursor is the server's, not a page number we invented.
      const collected: InventoryInstanceWire[] = [];
      let cursor: string | undefined;
      let version = 0;
      let allStacks: InventoryResponse['stacks'] = [];
      for (let page = 0; page < 50; page += 1) {
        const response: InventoryResponse = await fetchInventory(
          cursor === undefined ? { limit: 100 } : { limit: 100, cursor },
        );
        collected.push(...response.instances);
        allStacks = response.stacks;
        version = response.loadoutVersion;
        if (!response.nextCursor) {
          break;
        }
        cursor = response.nextCursor;
      }
      // Artifacts come from their own endpoint: they are not equipment, they
      // are derived state - which copy is active, what it contributes, how
      // many merges are available - and the server derives all of it.
      const artifactState = await fetchArtifacts();
      // Equipment likewise has its own endpoint, and for a stronger reason: an
      // inventory row cannot carry a roll, and every effective value on a
      // component is derived from one. The server sends the finished numbers.
      const equipment = await fetchEquipment();
      setItems(collected);
      setStacks(allStacks);
      setArtifacts(artifactState);
      setEquipmentState(equipment);
      setLoadoutVersion(version);
      setStatus('ready');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof HuntApiError && error.isUnauthorized
          ? 'Sua sessão expirou.'
          : 'Não foi possível carregar o inventário.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Confirma a incorporação, e depois relê as DUAS listas (gate V28F.C).
   *
   * A view das joias volta na própria resposta - o coração gasto desaparece
   * dela sozinho. O inventário não: a contagem que a carta da nave mostra vive
   * noutro contrato, e sem esta releitura o jogador via a prateleira certa e a
   * nave errada até recarregar a página. Que é exactamente o que o gate proíbe.
   */
  const runIncorporation = useCallback(
    async (jewelInstanceId: string, shipInstanceId: string) => {
      setIncorporationStatus('busy');
      setIncorporationFailure(null);
      const outcome = await jewels.incorporate(jewelInstanceId, shipInstanceId);
      if (outcome.ok) {
        setIncorporationStatus('done');
        await load();
        return;
      }
      /*
       * De volta a `idle`, e não a um estado de erro próprio.
       *
       * O modal fica aberto e o botão volta a abrir: a recusa pode ser deste
       * momento - outra aba anunciou a nave - e a mesma intenção repetida
       * reutiliza a chave. Um estado terminal obrigaria a recomeçar do zero
       * uma escolha que continua válida.
       */
      setIncorporationStatus('idle');
      setIncorporationFailure(incorporationFailureText(outcome.reason));
      /*
       * E o inventário relê TAMBÉM numa recusa.
       *
       * O hook das joias já relê a prateleira, mas as naves vêm do inventário -
       * e é a nave que mudou. Sem isto, `ship_reserved` mostrava a frase e
       * deixava a nave na lista como se ainda fosse elegível: o jogador lia
       * "esta nave está reservada" ao lado de uma carta que dizia o contrário,
       * e o botão voltava a abrir para uma tentativa que ia falhar outra vez.
       *
       * Relê-se em vez de se marcar a nave à mão: o erro diz o que falhou, não
       * diz como está o cofre agora.
       */
      await load();
    },
    [jewels, load],
  );

  // A successful equip (or an explicit retry) reloads from the server. There
  // is deliberately no optimistic local edit: the vault decides.
  useEffect(() => {
    if (refreshToken > 0 || reloadToken > 0) {
      void load();
    }
  }, [refreshToken, reloadToken, load]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    /**
     * Clicking outside the panel closes it.
     *
     * A document listener rather than a full-screen button: an invisible
     * button covering the viewport would swallow every click meant for the
     * HUD, the metrics and the Inventory button itself - the panel is not
     * allowed to take the interface hostage while it is open. Keyboard and
     * screen-reader users close it with Escape or the × button, both of which
     * are real controls.
     */
    const onPointerDown = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) {
        return;
      }
      // The Inventory button is excluded: it is a TOGGLE, and closing here
      // would let its own handler immediately reopen the panel, so pressing
      // it while open would appear to do nothing.
      const toggle = document.querySelector('[data-testid="inventory-toggle"]');
      if (toggle?.contains(event.target)) {
        return;
      }
      /*
       * A modal this panel opened is INSIDE it, whatever the DOM says.
       *
       * The skin roulette's reveal is a portal into `document.body` - it has to
       * be, because a `position: fixed` child of a transformed ancestor resolves
       * against that ancestor rather than the viewport. React sends its events
       * up the React tree, but this listener is on `document` in the CAPTURE
       * phase, so it sees the raw DOM target and the portal looked like the
       * outside world: clicking `PULAR ANIMAÇÃO` closed the whole inventory
       * underneath the player's own result.
       *
       * The marker is a declaration rather than a list of ids, so a modal added
       * next month is covered by opting in rather than by somebody remembering
       * to edit this line.
       */
      const element = event.target instanceof Element ? event.target : event.target.parentElement;
      if (element?.closest('[data-inventory-portal]')) {
        return;
      }
      // Resolved from the DOM at click time rather than from a ref, so the
      // listener has no dependency on a mutable value React cannot track.
      const panel = document.querySelector('[data-testid="inventory-panel"]');
      if (panel && !panel.contains(event.target)) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    // Capture phase: a click that closes the panel should not also activate
    // whatever sits underneath it.
    document.addEventListener('mousedown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [onClose]);

  const visible = useMemo(
    () => items.filter((item) => matches(item, tab, rarity, availability)).sort(compareItems),
    [items, tab, rarity, availability],
  );

  const selected = useMemo(
    () => visible.find((item) => item.itemId === selectedId) ?? null,
    [visible, selectedId],
  );

  // Padding cells so the last row still reads as a grid. Presentation only:
  // they are not capacity, and nothing can be bought to fill them. Keyed by a
  // stable string rather than an array index, which React would reuse wrongly.
  const paddingKeys = useMemo(() => {
    if (visible.length === 0) {
      return [];
    }
    const remainder = visible.length % GRID_COLUMNS;
    const count = remainder === 0 ? 0 : GRID_COLUMNS - remainder;
    return Array.from({ length: count }, (_, index) => `pad-${visible.length + index}`);
  }, [visible.length]);

  const equip = useCallback(
    async (itemId: string) => {
      setEquipping(itemId);
      setEquipError(null);
      try {
        await onEquip(itemId, loadoutVersion);
      } catch (error) {
        // A conflict means somebody equipped first: reload rather than leave
        // the player looking at a stale list.
        if (error instanceof HuntApiError && error.isConflict) {
          setEquipError('O equipamento mudou. Atualizando…');
          setReloadToken((token) => token + 1);
        } else {
          setEquipError('Não foi possível equipar.');
        }
      } finally {
        setEquipping(null);
      }
    },
    [onEquip, loadoutVersion],
  );

  const combine = useCallback(
    async (first: string, second: string) => {
      setCombining(true);
      setCombineError(null);
      try {
        await onCombine(first, second);
        // Reloaded from the server, never patched locally: the vault decides
        // which copies were spent and what replaced them.
        setReloadToken((token) => token + 1);
      } catch (error) {
        setCombineError(
          error instanceof HuntApiError && error.isConflict
            ? 'Essas cópias mudaram. Atualizando…'
            : 'Não foi possível combinar.',
        );
        setReloadToken((token) => token + 1);
      } finally {
        setCombining(false);
      }
    },
    [onCombine],
  );

  /**
   * Pet actions. Nothing is applied locally first: the vault decides which pet
   * is active and what an evolution produced, and this only renders the answer.
   */
  /**
   * Reads the palette.
   *
   * Called when the tab opens and again after anything that could have
   * changed it - which is better than a token in the dependency list: the
   * refetch happens because a sphere was spent, not because a number ticked.
   */
  const reloadPalette = useCallback(async () => {
    try {
      setPalette(await fetchPetPalette());
    } catch {
      // A failed read leaves the previous palette on screen: it describes
      // colours, and a stale swatch beats an empty panel.
    }
  }, []);

  const runPetAction = useCallback(
    async (action: () => Promise<void>, failure: string) => {
      setPetBusy(true);
      setPetError(null);
      try {
        await action();
        /*
         * The palette is re-read after EVERY pet action, not only an unlock.
         *
         * Feeding moves a food stack and evolving can move an appearance, so a
         * palette read only when the tab opened went stale the moment anything
         * was spent - which is how a swatch ended up advertising an item the
         * player no longer had.
         */
        await reloadPalette();
      } catch (error) {
        setPetError(
          error instanceof HuntApiError && error.isConflict ? 'O pet mudou. Atualizando…' : failure,
        );
      } finally {
        setPetBusy(false);
      }
    },
    [reloadPalette],
  );

  useEffect(() => {
    if (tab !== 'pet') {
      return;
    }
    void reloadPalette();
  }, [tab, reloadPalette]);

  /*
   * The wardrobe is read when its tab opens, and never before.
   *
   * Nothing else on this panel needs the skin contract, so fetching it on mount
   * would be a request every player pays for and most never use. Re-reading on
   * each open is also what keeps two tabs honest: whatever the other one did to
   * the slot, this one starts from the server's answer.
   */
  const refreshWardrobe = wardrobe.refresh;
  useEffect(() => {
    if (tab !== 'skin') {
      return;
    }
    void refreshWardrobe();
  }, [tab, refreshWardrobe]);

  /* ---------------------------------------------------------------- */
  /*  Equipment                                                       */
  /* ---------------------------------------------------------------- */

  const selectedEquipmentState =
    equipmentState?.owned.find((item) => item.itemId === selectedEquipment) ?? null;

  /**
   * Every equipment action, through one funnel.
   *
   * The answer IS the new state, adopted verbatim, so there is no window in
   * which a button and the vault disagree and nothing optimistic is ever shown.
   * The hunt does not need a reload either: the response carries the SAME
   * session, reconfigured in place, and the arena behind this panel keeps
   * running.
   */
  const runEquipmentAction = useCallback(
    async (
      action: () => Promise<{
        slots: EquipmentListResponse['slots'];
        owned: EquipmentListResponse['owned'];
        stabilizers: string;
        loadoutVersion: number;
      }>,
      failure: string,
    ) => {
      setEquipmentBusy(true);
      setEquipmentError(null);
      try {
        const response = await action();
        setEquipmentState({
          slots: response.slots,
          owned: response.owned,
          stabilizers: response.stabilizers,
          loadoutVersion: response.loadoutVersion,
        });
        setLoadoutVersion(response.loadoutVersion);
      } catch (error) {
        setEquipmentError(
          error instanceof HuntApiError && error.isConflict
            ? 'O equipamento mudou. Atualize e tente de novo.'
            : failure,
        );
      } finally {
        setEquipmentBusy(false);
      }
    },
    [],
  );

  /**
   * The quote, re-read whenever the selected component or its level changes.
   *
   * Never computed here. The price, the chance, the risk and the estimated
   * Power all come from the server, and a stale one is refused by the attempt
   * itself through `expectedLevel`.
   */
  useEffect(() => {
    if (tab !== 'equipment' || !selectedEquipmentState) {
      setEquipmentQuote(null);
      return;
    }
    let cancelled = false;
    void fetchUpgradeQuote(selectedEquipmentState.itemId)
      .then((response) => {
        if (!cancelled) {
          setEquipmentQuote(response.quote);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEquipmentQuote(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tab, selectedEquipmentState]);

  const selectedPetState = pets?.pets.find((pet) => pet.definitionId === selectedPet) ?? null;

  /**
   * Keeps the selection honest across server answers.
   *
   * Three rules, and each one is a decision rather than a convenience:
   *
   * - The selection SURVIVES a refresh. A reload of the pet list is not a
   *   reason to move where the next food goes.
   * - When the selected copy is gone and exactly one copy is NEW, the new one
   *   is selected: that is an evolution, and the replacement is unambiguously
   *   what the player was working on.
   * - When it is gone and nothing replaced it - retired, traded away - the
   *   selection is CLEARED and a new choice is required. Redirecting silently
   *   would spend the next food on a pet nobody chose.
   */
  useEffect(() => {
    const copies = selectedPetState?.copies ?? [];
    const ids = copies.map((copy) => copy.instanceId);
    const previous = previousCopyIds.current;
    previousCopyIds.current = ids;

    if (ids.length === 0) {
      setSelectedCopy(null);
      setSecondaryCopy(null);
      return;
    }
    setSelectedCopy((current) => {
      if (current !== null && ids.includes(current)) {
        return current;
      }
      if (current === null) {
        // First load of this pet: start on the active copy when there is one,
        // which is the pet the player is looking at in the arena.
        return copies.find((copy) => copy.active)?.instanceId ?? null;
      }
      const appeared = ids.filter((id) => !previous.includes(id));
      return appeared.length === 1 ? (appeared[0] ?? null) : null;
    });
    setSecondaryCopy((current) => (current !== null && ids.includes(current) ? current : null));
  }, [selectedPetState]);
  const artifactList = artifacts?.artifacts ?? [];
  const selectedArtifactState =
    artifactList.find((artifact) => artifact.definitionId === selectedArtifact) ?? null;

  return (
    <div className="inv-backdrop" data-testid="inventory-backdrop">
      {/*
        The enlarged variant is a CLASS, not a `:has()` on the tab's markup.

        Both would work, but a class is the thing a test can assert and a
        reader can find: "which tabs get the big modal" is answered here, in
        one expression, rather than inferred from a selector three thousand
        lines into a stylesheet. It also makes the wrong answer impossible to
        reach by accident - a future tab that happens to render an equipment
        card does not silently inherit a 1440px modal.
      */}
      <section
        className={inventoryPanelClass(tab)}
        data-testid="inventory-panel"
        data-variant={tab === 'equipment' ? 'wide' : 'default'}
        aria-labelledby={titleId}
      >
        <header className="inv-header">
          <h2 id={titleId}>Inventário</h2>
          <button
            type="button"
            className="inv-close"
            data-testid="inventory-close"
            aria-label="Fechar inventário"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <nav className="inv-tabs" aria-label="Categorias">
          {TABS.map((value) => (
            <button
              key={value}
              type="button"
              className="inv-tab"
              data-testid={`inv-tab-${value}`}
              aria-pressed={tab === value}
              onClick={() => {
                setTab(value);
                setSelectedId(null);
              }}
            >
              {CATEGORY_LABEL[value]}
            </button>
          ))}
        </nav>

        {/* Artifacts are filtered by nothing: there are exactly ten, they are
            never equipped, and "available" has no meaning for a passive. */}
        {/* Equipment is filtered by nothing either: there are five slots and a
            short list beside them, and a rarity filter over five items is
            furniture rather than a tool. */}
        <div
          className="inv-filters"
          hidden={
            tab === 'artifact' ||
            tab === 'pet' ||
            tab === 'equipment' ||
            tab === 'skin' ||
            tab === 'jewel' ||
            /* As cartas trazem os próprios estados por palavras; um filtro de
               raridade sobre dezasseis linhas seria mobília. */
            tab === 'card'
          }
        >
          <label>
            <span>Raridade</span>
            <select
              data-testid="inv-filter-rarity"
              value={rarity}
              onChange={(event) => setRarity(event.target.value as Rarity | 'any')}
            >
              <option value="any">Todas</option>
              {RARITY_ORDER.map((value) => (
                <option key={value} value={value}>
                  {rarityLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Estado</span>
            <select
              data-testid="inv-filter-availability"
              value={availability}
              onChange={(event) => setAvailability(event.target.value as Availability)}
            >
              {(Object.keys(AVAILABILITY_LABEL) as Availability[]).map((value) => (
                <option key={value} value={value}>
                  {AVAILABILITY_LABEL[value]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="inv-body">
          {tab === 'pet' ? (
            <>
              <PetsTab
                pets={pets}
                manifest={manifest}
                selectedId={selectedPet}
                onSelect={setSelectedPet}
              />
              <PetDetailsPanel
                pet={selectedPetState}
                manifest={manifest}
                busy={petBusy}
                error={petError}
                onActivate={(instanceId) => {
                  void runPetAction(
                    () => onActivatePet(instanceId),
                    'Não foi possível mudar o pet.',
                  );
                }}
                onEvolve={(first, second) => {
                  void runPetAction(() => onEvolvePet(first, second), 'Não foi possível evoluir.');
                }}
                palette={palette}
                selectedCopyId={selectedCopy}
                onSelectCopy={(instanceId) => {
                  setSelectedCopy(instanceId);
                  // A new primary invalidates the secondary: pairing a copy
                  // with itself is not an evolution.
                  setSecondaryCopy(null);
                }}
                secondaryCopyId={secondaryCopy}
                onSelectSecondary={(instanceId) =>
                  setSecondaryCopy(instanceId === selectedCopy ? null : instanceId)
                }
                onFeed={(petInstanceId, foodDefinitionId) => {
                  void runPetAction(
                    () => onFeedPet(petInstanceId, foodDefinitionId),
                    'Não foi possível alimentar.',
                  );
                }}
                onUnlock={(unlockDefinitionId) => {
                  void runPetAction(async () => {
                    // Adopted verbatim: the answer IS the new palette, so
                    // there is no window in which the button and the vault
                    // disagree, and nothing optimistic is ever shown.
                    const response = await onUnlockPetAppearance(unlockDefinitionId);
                    setPalette(response.palette);
                  }, 'Não foi possível desbloquear.');
                }}
                onApply={(petInstanceId, energyId, finishId) => {
                  void runPetAction(
                    () => onApplyPetAppearance(petInstanceId, energyId, finishId),
                    'Não foi possível aplicar a aparência.',
                  );
                }}
              />
            </>
          ) : status === 'ready' && tab === 'equipment' && equipmentState ? (
            <>
              <EquipmentTab
                slots={equipmentState.slots}
                owned={equipmentState.owned}
                selectedId={selectedEquipment}
                manifest={manifest}
                focus={equipmentFocus}
                onFocus={setEquipmentFocus}
                onSelect={(itemId) => {
                  setSelectedEquipment(itemId);
                  setEquipmentOutcome(null);
                  setEquipmentError(null);
                }}
                onSelectSlot={() => {
                  // An empty slot has nothing to describe. Clearing the
                  // selection is the honest answer rather than leaving the
                  // panel showing an item the player is not looking at.
                  setSelectedEquipment(null);
                  setEquipmentOutcome(null);
                }}
              />
              <EquipmentDetails
                item={selectedEquipmentState}
                manifest={manifest}
                quote={equipmentQuote}
                busy={equipmentBusy}
                error={equipmentError}
                lastOutcome={equipmentOutcome}
                currency={upgradeCurrency}
                onCurrency={setUpgradeCurrency}
                useStabilizer={useStabilizer}
                onUseStabilizer={setUseStabilizer}
                onEquip={(itemId) => {
                  setEquipmentOutcome(null);
                  void runEquipmentAction(
                    () =>
                      equipComponent({
                        itemInstanceId: itemId,
                        expectedLoadoutVersion: equipmentState.loadoutVersion,
                        idempotencyKey: `equip:${itemId}:${Date.now()}`,
                      }),
                    'Não foi possível equipar.',
                  );
                }}
                onUnequip={(component: EquipmentComponent) => {
                  setEquipmentOutcome(null);
                  void runEquipmentAction(
                    () =>
                      unequipComponent({
                        component,
                        expectedLoadoutVersion: equipmentState.loadoutVersion,
                        idempotencyKey: `unequip:${component}:${Date.now()}`,
                      }),
                    'Não foi possível remover.',
                  );
                }}
                onUpgrade={(itemId) => {
                  const level = selectedEquipmentState?.level ?? 1;
                  void runEquipmentAction(async () => {
                    const response = await upgradeComponent({
                      itemInstanceId: itemId,
                      // The level the client BELIEVES it is upgrading from.
                      // Another tab that succeeded first makes this a refusal
                      // rather than a payment at the wrong price.
                      expectedLevel: level,
                      currency: upgradeCurrency,
                      useStabilizer,
                      idempotencyKey: `upgrade:${itemId}:${level}:${Date.now()}`,
                    });
                    setEquipmentOutcome(response.outcome);
                    // A destroyed component has no card left to select.
                    if (response.item === null) {
                      setSelectedEquipment(null);
                    }
                    return response;
                  }, 'Não foi possível aprimorar.');
                }}
              />
            </>
          ) : status === 'ready' && tab === 'artifact' && artifacts ? (
            <>
              <ArtifactsTab
                artifacts={artifacts.artifacts}
                manifest={manifest}
                selectedId={selectedArtifact}
                onSelect={setSelectedArtifact}
              />
              <ArtifactDetails
                artifact={selectedArtifactState}
                manifest={manifest}
                combining={combining}
                error={combineError}
                onCombine={(first, second) => void combine(first, second)}
              />
            </>
          ) : tab === 'card' ? (
            <CardsTab
              collection={items.filter((item) => item.category === 'card').sort(compareItems)}
              slots={cards.state.slots}
              loading={cards.state.loading}
              busy={cards.state.busy}
              error={cards.state.error}
              failure={cards.state.failure}
              pvpActive={pvpActive}
              huntActive={huntActive}
              selectedId={selectedCardId}
              onSelect={setSelectedCardId}
              onEquip={(slot, itemId) => {
                /* A versão que o pedido cita é a da ÚLTIMA resposta, nunca uma
                   contagem local. */
                void cards
                  .equip(slot, itemId, cards.state.loadoutVersion)
                  .then(() => setReloadToken((token) => token + 1));
              }}
              onUnequip={(slot) => {
                void cards
                  .unequip(slot, cards.state.loadoutVersion)
                  .then(() => setReloadToken((token) => token + 1));
              }}
              onCombine={(first, second) => {
                void cards.combine(first, second).then((outcome) => {
                  if (outcome.ok) {
                    /* As duas entradas foram aposentadas e nasceu uma terceira:
                       a coleção tem de ser relida, nunca remendada. */
                    setSelectedCardId(null);
                  }
                  setReloadToken((token) => token + 1);
                });
              }}
            />
          ) : tab === 'jewel' ? (
            <JewelPanel
              view={jewels.state.view}
              assetBaseUrl={JEWEL_ASSET_BASE_URL}
              skinLabels={skinLabels}
              loading={jewels.state.loading}
              error={jewels.state.error}
              selectedInstanceId={selectedJewelId}
              /* Clicar na peça já escolhida desfaz a escolha. */
              onSelect={(instanceId) =>
                setSelectedJewelId((current) => (current === instanceId ? null : instanceId))
              }
              onIncorporate={(jewelInstanceId) => {
                setIncorporationStatus('idle');
                setIncorporationFailure(null);
                setIncorporatingHeart(jewelInstanceId);
              }}
            />
          ) : tab === 'skin' ? (
            <div className="skin-tab">
              {/*
               * The roulette, above the shelf it fills.
               *
               * Same panel on purpose: a confirmed roll has to appear on the
               * shelf without a reload, and the shelf's own hook is already
               * mounted here. Anywhere else this would need messaging between
               * two overlays for a state the server already answered with.
               *
               * Mounted only while this tab is open, so a player who never
               * opens the wardrobe never pays for the banner request.
               */}
              <SkinGachaBanner
                artFor={skinArtFor}
                onOpenWardrobe={() => {
                  // Looking, never equipping. The heading takes focus so a
                  // keyboard and a screen reader both land on the new copy.
                  wardrobeHeading.current?.focus();
                }}
                onRolled={() => {
                  /*
                   * Re-read, never predict.
                   *
                   * The answer names one instance; the shelf is the server's
                   * whole list, and appending locally would be an optimistic
                   * write of exactly the kind the wardrobe was built to avoid.
                   */
                  void refreshWardrobe();
                }}
              />
              {/*
               * Where focus lands after a reveal.
               *
               * `tabIndex={-1}` makes a heading programmatically focusable
               * without putting it in the tab order, which is the standard way
               * to move a screen reader to a region that just changed.
               */}
              <h4 className="skin-tab-heading" ref={wardrobeHeading} tabIndex={-1}>
                Suas skins
              </h4>
              <SkinWardrobe
                artFor={skinArtFor}
                busy={wardrobe.state.busy}
                catalogue={wardrobe.state.catalogue}
                error={wardrobe.state.error}
                onEquip={(instanceId) => void wardrobe.equip(instanceId)}
                onUnequip={() => void wardrobe.unequip()}
                owned={wardrobe.state.owned}
                pendingInstanceId={wardrobe.state.pendingInstanceId}
                renderSockets={(skinInstanceId) => {
                  const sockets = jewels.state.view?.skins.find(
                    (entry) => entry.skinInstanceId === skinInstanceId,
                  );
                  if (!sockets) {
                    return null;
                  }
                  /*
                   * A peça SELECIONADA, e a primeira livre quando não há
                   * nenhuma escolhida.
                   *
                   * O recuo é determinístico porque a lista vem ordenada do
                   * servidor, e existe para o botão nunca ficar morto quando o
                   * jogador chega ao vestiário sem ter escolhido nada. O que o
                   * pedido envia é sempre a IDENTIDADE da peça - nunca a sua
                   * posição no ecrã, e nunca a do assento.
                   */
                  const freeJewels = (jewels.state.view?.owned ?? []).filter(
                    (jewel) => jewel.socketMode === 'socketed' && jewel.seat === null,
                  );
                  const free =
                    freeJewels.find((jewel) => jewel.instanceId === selectedJewelId) ??
                    freeJewels[0];
                  return (
                    <JewelSocketRow
                      skin={sockets}
                      jewels={jewels.state.view?.owned ?? []}
                      assetBaseUrl={JEWEL_ASSET_BASE_URL}
                      busy={jewels.state.busy}
                      onInsert={(socketIndex) => {
                        if (free) {
                          void jewels.insert(skinInstanceId, socketIndex, free.instanceId);
                        }
                      }}
                      onRemove={(socketIndex) => void jewels.remove(skinInstanceId, socketIndex)}
                      onSwap={(socketIndex) => {
                        if (free) {
                          void jewels.swap(skinInstanceId, socketIndex, free.instanceId);
                        }
                      }}
                    />
                  );
                }}
              />
            </div>
          ) : status === 'loading' ? (
            <p className="inv-state" data-testid="inventory-loading">
              Carregando inventário…
            </p>
          ) : status === 'error' ? (
            <div className="inv-state" data-testid="inventory-error" role="alert">
              <p>{errorMessage}</p>
              <button type="button" data-testid="inventory-retry" onClick={() => void load()}>
                Tentar novamente
              </button>
            </div>
          ) : visible.length === 0 ? (
            <p className="inv-state" data-testid="inventory-empty">
              Nenhum item nesta categoria.
            </p>
          ) : (
            <div className="inv-grid" data-testid="inventory-grid">
              {visible.map((item) => (
                <ItemCell
                  key={item.itemId}
                  item={item}
                  manifest={manifest}
                  selected={item.itemId === selectedId}
                  onSelect={() => setSelectedId(item.itemId)}
                />
              ))}
              {paddingKeys.map((key) => (
                // Presentation only: an empty cell is not capacity, and
                // nothing can be bought to fill one.
                <span
                  key={key}
                  className="inv-cell inv-cell-empty"
                  data-testid="inv-cell-empty"
                  aria-hidden="true"
                />
              ))}
            </div>
          )}

          {/*
            The generic item panel belongs to the generic tabs.

            The pet and artifact tabs were excluded when they gained panels of
            their own: this one sat underneath saying "Selecione um item" at a
            player who had just selected one. The equipment tab was not, and it
            cost more than a duplicated sentence - `.inv-body` is a two-column
            grid, so a third child wrapped onto a SECOND ROW and took 79px plus
            a gap out of the row above it. That is why the equipment panel
            measured 368px inside a 474px body and laid its footer out below its
            own box, where `overflow: hidden` clipped "Aprimorar" away entirely.
          */}
          {tab === 'pet' ||
          tab === 'artifact' ||
          tab === 'equipment' ||
          tab === 'skin' ||
          tab === 'jewel' ? null : (
            <InventoryDetails
              item={selected}
              loadout={loadout}
              manifest={manifest}
              equipping={equipping === selected?.itemId}
              error={equipError}
              onEquip={(itemId) => void equip(itemId)}
            />
          )}
        </div>

        {stacks.length > 0 ? (
          <footer className="inv-stacks" data-testid="inventory-stacks">
            {stacks.map((stack) => (
              <span key={stack.definitionId}>
                {stack.definitionId}: {stack.available}
              </span>
            ))}
          </footer>
        ) : null}
      </section>
      {/*
       * A incorporação, num portal por cima de tudo (gate V28F.C).
       *
       * Montada aqui e não dentro da aba: o painel é quem tem as naves, quem
       * tem o hook das joias e quem sabe recarregar as duas listas. Dentro da
       * aba, a janela morreria com ela ao primeiro clique noutra categoria.
       */}
      {incorporatingHeart ? (
        <PrimordialIncorporationDialog
          heartLabel="O Coração Primordial"
          ships={shipOptions}
          status={incorporationStatus}
          failure={incorporationFailure}
          onIncorporate={(shipInstanceId) =>
            void runIncorporation(incorporatingHeart, shipInstanceId)
          }
          /*
           * De volta ao botão daquele Coração, resolvido AO FECHAR.
           *
           * O painel é quem sabe que o gesto nasceu na prateleira e qual cópia
           * foi carregada. E a resolução é tardia porque uma recusa relê o
           * inventário: a prateleira desenha-se outra vez, e o nó antigo já não
           * está no documento - devolver-lhe o foco seria devolvê-lo a nada.
           *
           * Quando o Coração foi GASTO aquele botão já não existe - e nesse caso
           * o foco não pode simplesmente cair no `<body>`, que é recomeçar no
           * topo do documento a seguir a uma acção no meio de uma prateleira. A
           * prateleira é o sítio certo: é onde a pessoa estava a trabalhar e é
           * onde está o resultado.
           */
          returnFocus={() =>
            document.querySelector<HTMLElement>(
              `[data-testid="jewel-incorporate-${incorporatingHeart}"]`,
            ) ?? document.querySelector<HTMLElement>('[data-testid="jewel-sovereign-shelf"] button')
          }
          onClose={() => {
            setIncorporatingHeart(null);
            setIncorporationStatus('idle');
            setIncorporationFailure(null);
          }}
        />
      ) : null}
    </div>
  );
}
